import { ArrowsRightLeftIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { usePipelineModeSwitch, usePipelineTypeOptions } from 'hooks/usePipelineMode'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

/**
 * Global Origination / Servicing switch.
 *
 * The mode is a filter on the pipeline grid as well, but it drives the whole app - the
 * servicing header items, the Reports link and the loan screens reached from them - so it
 * is switchable from anywhere here too. Both controls write the same store field.
 */
export const PipelineModeSwitch = ({
  variant = 'chip',
  onSwitched,
}: {
  variant?: 'chip' | 'list'
  onSwitched?: () => void
}) => {
  const { canSwitchPipelineType, pipelineTypeOptions } = usePipelineTypeOptions()
  const { pipelineType, switchTo } = usePipelineModeSwitch()
  const defaultPipeline = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.GlobalSettingV2]?.defaultPipeline,
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    const onClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  if (!canSwitchPipelineType) return null

  const options = Object.keys(pipelineTypeOptions)
  // Until the pipeline has loaded once there is no stored mode, so show the one it will
  // land on rather than an empty chip.
  const activeType = pipelineType || (options.includes(defaultPipeline) ? defaultPipeline : options[0])
  const currentLabel = pipelineTypeOptions[activeType]

  const onSelect = (type: string) => {
    setIsOpen(false)
    switchTo(type)
    onSwitched?.()
  }

  // Stacked list for the mobile menu, where there is no room for a popover.
  if (variant === 'list') {
    return (
      <div className="flex flex-col gap-1">
        {options.map((type) => (
          <button
            key={type}
            type="button"
            className={`flex items-center justify-between rounded-md px-3 py-2 text-base font-bold transition-colors ${
              type === activeType
                ? 'bg-shade-blue/10 text-shade-blue'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
            onClick={() => onSelect(type)}
          >
            {pipelineTypeOptions[type]}
            {type === activeType && <CheckIcon className="h-4 w-4" />}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="PipelineModeSwitch-container relative" ref={containerRef}>
      <button
        type="button"
        className="flex max-w-[11rem] items-center gap-1.5 rounded-full bg-shade-blue/10 px-3 py-1.5 text-xs font-semibold text-shade-blue transition-colors hover:bg-shade-blue/20 focus:outline-none focus:ring-2 focus:ring-sky-200"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={`View: ${currentLabel}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <ArrowsRightLeftIcon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{currentLabel}</span>
        <ChevronDownIcon className={`h-3 w-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 z-[60] mt-2 w-56 overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-2xl">
          <div className="px-3 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Switch View
          </div>
          <ul className="max-h-72 overflow-y-auto p-1.5 pt-0" role="listbox" aria-label="Switch view">
            {options.map((type) => (
              <li key={type}>
                <button
                  type="button"
                  role="option"
                  aria-selected={type === activeType}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                    type === activeType
                      ? 'bg-shade-blue/10 font-semibold text-shade-blue'
                      : 'text-gray-700 hover:bg-slate-100'
                  }`}
                  onClick={() => onSelect(type)}
                >
                  <span className="truncate">{pipelineTypeOptions[type]}</span>
                  {type === activeType && <CheckIcon className="h-4 w-4 flex-shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
